import { useId } from "react";

import { FigureFrame } from "./FigureFrame";
import type { ResearchFigureProps } from "./types";

const ink = "var(--figure-ink, currentColor)";
const accent = "var(--figure-accent, currentColor)";
const surface = "var(--figure-surface, transparent)";

export function AnonymousPeersFigure({
  className,
  caption,
  title = "How anonymous peers shape purchase decisions",
  description = "Anonymous peer purchases provide information that influences a focal consumer's purchase. The mechanism is informational rather than competitive, and is stronger for inexperienced consumers.",
}: ResearchFigureProps) {
  const markerId = `peers-arrow-${useId().replace(/:/g, "")}`;

  return (
    <FigureFrame
      className={className}
      caption={caption}
      title={title}
      description={description}
      viewBox="0 0 760 420"
    >
      <defs>
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={accent} />
        </marker>
      </defs>

      <text x="32" y="36" fill={ink} fontSize="12" fontWeight="650" letterSpacing="1.6">
        PURCHASE SEQUENCE
      </text>
      <line x1="32" y1="54" x2="728" y2="54" stroke={ink} strokeWidth="1.25" />

      <g fill={surface} stroke={ink} strokeWidth="1.25">
        <rect x="32" y="104" width="196" height="104" />
        <rect x="282" y="104" width="196" height="104" />
        <rect x="532" y="104" width="196" height="104" />
      </g>
      <g fill={ink} textAnchor="middle">
        <text x="130" y="143" fontSize="17" fontWeight="650">Anonymous peer</text>
        <text x="130" y="170" fontSize="13">observed purchase</text>
        <text x="380" y="143" fontSize="17" fontWeight="650">Information</text>
        <text x="380" y="170" fontSize="13">market signal</text>
        <text x="630" y="143" fontSize="17" fontWeight="650">Focal consumer</text>
        <text x="630" y="170" fontSize="13">purchase decision</text>
      </g>
      <g fill="none" stroke={accent} strokeWidth="1.5">
        <path d="M 240 156 H 268" markerEnd={`url(#${markerId})`} />
        <path d="M 490 156 H 518" markerEnd={`url(#${markerId})`} />
      </g>

      <g transform="translate(32 278)">
        <text x="0" y="0" fill={ink} fontSize="12" fontWeight="650" letterSpacing="1.6">
          MECHANISM CHECK
        </text>
        <line x1="0" y1="18" x2="696" y2="18" stroke={ink} strokeOpacity=".25" />
        <text x="0" y="60" fill={accent} fontSize="18" fontWeight="700">
          Informational
        </text>
        <text x="191" y="60" fill={ink} fontSize="18">
          rather than competitive
        </text>

        <line x1="0" y1="88" x2="696" y2="88" stroke={ink} strokeOpacity=".18" />
        <text x="0" y="126" fill={ink} fontSize="15">
          Consumer experience
        </text>
        <text x="696" y="126" fill={accent} fontSize="15" fontWeight="700" textAnchor="end">
          stronger effect when experience is low
        </text>
      </g>
    </FigureFrame>
  );
}
